import { DEFAULT_TEAM_NAME } from "@/constants";
import { LoadingView } from "@/features/Auth/components/LoadingView";
import { useAppNavigation } from "@/features/Navigation/hooks/useAppNavigation";
import { usePermissions } from "@/features/Permissions/hooks/usePermissions";
import {
  create_team_event,
  delete_team_event,
  remove_team_member_event,
  resend_invite_event,
  update_team_member_role_event,
} from "@/services/posthog/events";
import { useAnalytics } from "@/services/posthog/hooks";
import {
  Button,
  Divider,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { SidebarItem } from "@pufflig/prompt-studio-ui";
import { useEffect, useState } from "react";
import { cancelInvite } from "../../actions/cancelInvite";
import { createTeam } from "../../actions/createTeam";
import { deleteTeam } from "../../actions/deleteTeam";
import { removeTeamMember } from "../../actions/removeTeamMember";
import { renameTeam } from "../../actions/renameTeam";
import { resendInvite } from "../../actions/resendInvite";
import { setTeamMemberRole } from "../../actions/setTeamMemberRole";
import { usePendingInvites } from "../../hooks/usePendingInvites";
import { useTeamMembers } from "../../hooks/useTeamMembers";
import { useTeams } from "../../hooks/useTeams";
import { EmptyTeamList } from "../EmptyTeamList/EmptyTeamList";
import { InviteMemberModal } from "../InviteMemberModal/InviteMemberModal";
import { TeamHeader } from "../TeamHeader/TeamHeader";
import { TeamMemberList } from "../TeamMemberList/TeamMemberList";

interface TeamsModalProps {
  teamId: string | null;
  isOpen: boolean;
  canCreateTeams: boolean;
  onClose: () => void;
  shouldCreateTeam?: boolean;
}

export const TeamsModal = (props: TeamsModalProps) => {
  const { isOpen, onClose, teamId, canCreateTeams, shouldCreateTeam } = props;
  const { isOpen: isInviteMemberOpen, onOpen: onInviteMemberOpen, onClose: onInviteMemberClose } = useDisclosure();
  const { navigateToTeam } = useAppNavigation();
  const { data: fetchedUserTeams, refetch: refetchTeams } = useTeams();
  const [userTeams, setUserTeams] = useState<{ role: string; id: string; name: string }[] | null>(null);
  const defaultSelectedTeamId = userTeams?.find((team) => team.id === teamId)?.id || userTeams?.[0]?.id || null;
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(defaultSelectedTeamId);
  const selectedTeam = userTeams?.find((team) => team.id === selectedTeamId);
  const [isLoading, setIsLoading] = useState(false);
  const { data: permissions } = usePermissions(selectedTeamId || undefined);
  const analytics = useAnalytics();

  const canEditSelectedTeam = permissions?.canEditTeam || false;

  useEffect(() => {
    if (fetchedUserTeams) {
      setUserTeams(fetchedUserTeams);
    }
  }, [fetchedUserTeams]);

  useEffect(() => {
    setSelectedTeamId(defaultSelectedTeamId);
  }, [defaultSelectedTeamId]);

  useEffect(() => {
    if (shouldCreateTeam) {
      onCreateTeam();
    }
  }, [shouldCreateTeam]);

  const { data: teamMembers = {} } = useTeamMembers(selectedTeamId || "");
  const { data: pendingInvites } = usePendingInvites(selectedTeamId || "");

  const teamMemberTable = Object.values(teamMembers).map((member) => ({
    id: member.id || "",
    member: { name: member.name || member.email, picture: member.picture },
    email: member.email || "",
    accessLevel: member.role as any,
    isPending: false,
  }));

  const teamInviteTable = (pendingInvites || []).map((invite) => ({
    id: invite.invite_id,
    member: { name: invite.email, picture: "" },
    email: invite.email,
    accessLevel: invite.role,
    isPending: true,
  }));

  const members = [...teamInviteTable, ...teamMemberTable];

  useEffect(() => {
    if (isOpen && !canCreateTeams && !userTeams?.length) {
      onClose();
    }
  }, [canCreateTeams, userTeams?.length, isOpen]);

  const onCreateTeam = async () => {
    setIsLoading(true);
    analytics.capture(create_team_event, { from: "sidebar" });
    const result = await createTeam(DEFAULT_TEAM_NAME);
    if (result?.teamId) {
      await refetchTeams();
      setSelectedTeamId(result.teamId);
    }
  };

  const onDeleteTeam = async () => {
    setIsLoading(true);
    if (selectedTeam?.id) {
      analytics.capture(delete_team_event, { from: "sidebar" });
      await deleteTeam(selectedTeam.id);
      await refetchTeams();
      setSelectedTeamId(defaultSelectedTeamId);
    }
    setIsLoading(false);
    onClose();
  };

  return (
    <>
      <Modal variant="large" size="6xl" isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader padding="spacing-lg">
            <Text textStyle="heading/2">Team settings</Text>
            <ModalCloseButton
              mt="4px"
              border="2px solid"
              borderColor="border.tertiary"
              borderRadius="radius-sm"
              py="1rem"
              px="1.125rem"
            />
          </ModalHeader>
          <ModalBody display="flex" flexDirection="row">
            <Stack minWidth="12rem">
              <SidebarItem
                key="team-and-members"
                label="Team & members"
                isSelected={true}
                onClick={() => {
                  if (selectedTeamId) navigateToTeam(selectedTeamId);
                }}
              />
            </Stack>
            <Flex>
              <Divider orientation="vertical" mx="4" height="100%" />
            </Flex>
            {isLoading && (
              <Flex direction="column" height="20rem" width="100%" position="relative">
                <LoadingView />
              </Flex>
            )}
            {!isLoading && !userTeams?.length && <EmptyTeamList onCreateTeam={onCreateTeam} />}
            {!isLoading && selectedTeamId && (
              <Flex direction="column" width="100%">
                <Flex mb="spacing-xl">
                  <TeamHeader
                    teamName={selectedTeam?.name || ""}
                    canEdit={canEditSelectedTeam}
                    onRename={async (newName) => {
                      if (selectedTeam?.id) {
                        await renameTeam(selectedTeam.id, newName);
                        await refetchTeams();
                      }
                    }}
                    onDelete={onDeleteTeam}
                  />
                </Flex>
                <Flex direction="column" flex="1">
                  <Flex alignItems="center" justifyContent="space-between" mb="spacing-lg">
                    <Text textStyle="heading/2">Team Members</Text>
                    <Button isDisabled={!canEditSelectedTeam} variant="primary" onClick={onInviteMemberOpen}>
                      Invite Members
                    </Button>
                  </Flex>
                  <Flex mb="6rem">
                    <TeamMemberList
                      isReadOnly={!canEditSelectedTeam}
                      onPermissionChange={(userId, role) => {
                        analytics.capture(update_team_member_role_event, { role, from: "team_member_table" });

                        const roleMap: Record<string, string> = {
                          viewer: "team_viewer",
                          editor: "team_editor",
                        };

                        setTeamMemberRole(selectedTeamId, userId, roleMap[role]);
                      }}
                      onRemoveTeamMember={(userId) => {
                        analytics.capture(remove_team_member_event, { from: "team_member_table" });
                        removeTeamMember(selectedTeamId, userId);
                      }}
                      onCancelInvite={cancelInvite}
                      onResendInvite={(inviteId) => {
                        analytics.capture(resend_invite_event, { from: "team_member_table" });
                        resendInvite(inviteId);
                      }}
                      data={members}
                    />
                  </Flex>
                </Flex>
              </Flex>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
      <InviteMemberModal isOpen={isInviteMemberOpen} onClose={onInviteMemberClose} teamId={selectedTeam?.id || ""} />
    </>
  );
};
